import { useEffect, useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { AvatarCircles } from '@rocco/components/avatar-circles';
import type { BuildHubOption } from '@ui-rocco/hooks/use-build-hubs';
import { getCollaboratorAvatar } from '@modules/build-hub/utils/get-collaborator-avatar';
import { InlineCreateBuildHubForm } from '~/modules/enquiry/component/build-hub/InlineCreateBuildHubForm';
import { BUILD_HUB_STAGE_CONFIG_API_ROUTE } from '~/routes/remix-api.build-hub.stage-config';
import type { StageConfiguration } from '@modules/build-hub/configs/stage-config';
import { GATE_FIELD_INPUT_CLASS } from '~/modules-rocco/user/component/auth-modal/GateFormControls';
import { projectSubtitle } from './project-summary';
import { ProjectThumbnail } from './ProjectThumbnail';

export interface EnquiryProjectPickerProps {
    projects: BuildHubOption[];
    /** The project currently attached to the enquiry. */
    selectedId?: string;
    onSelect: (id: string) => void;
    /** A project created here, so the host can hold it through the refetch. */
    onCreated?: (id: string) => void;
    /** Refetch the list once a project has been created into it. */
    onRefetch?: () => void;
    disabled?: boolean;
}

/**
 * Choosing which saved project rides along with the enquiry.
 *
 * Inline rather than a popover: the reader is mid-form, and a layer over the
 * top of it hides the enquiry they are deciding about. The list is already
 * loaded in full (capped at 100 server-side), so the search filters in memory
 * and answers on the keystroke.
 */
export const EnquiryProjectPicker = ({
    projects,
    selectedId,
    onSelect,
    onCreated,
    onRefetch,
    disabled = false,
}: EnquiryProjectPickerProps) => {
    const [keyword, setKeyword] = useState('');
    const [creating, setCreating] = useState(false);
    const [stageConfig, setStageConfig] = useState<StageConfiguration | null>(
        null,
    );
    const [configFailed, setConfigFailed] = useState(false);

    // Loaded on demand: most readers pick an existing project and never open
    // the create view at all.
    useEffect(() => {
        if (!creating || stageConfig) return;
        setConfigFailed(false);
        fetch(BUILD_HUB_STAGE_CONFIG_API_ROUTE)
            .then((response) => {
                if (!response.ok) throw new Error('Config unavailable');
                return response.json();
            })
            .then(setStageConfig)
            .catch(() => setConfigFailed(true));
    }, [creating, stageConfig]);

    const matches = useMemo(() => {
        const needle = keyword.trim().toLowerCase();
        if (!needle) return projects;
        // Title and place both: a reader looking for the Piha build is as
        // likely to type the suburb as the name they gave it.
        return projects.filter((project) =>
            [
                project.Title,
                project.ProjectScope,
                project.LocationData?.suburb,
                project.LocationData?.city,
                project.Location,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
                .includes(needle),
        );
    }, [projects, keyword]);

    if (creating) {
        if (stageConfig) {
            return (
                <InlineCreateBuildHubForm
                    stageConfig={stageConfig}
                    variant="gate"
                    onBuildHubCreated={(id) => {
                        (onCreated ?? onSelect)(id);
                        onRefetch?.();
                        setCreating(false);
                    }}
                    onBackToList={() => setCreating(false)}
                />
            );
        }
        // A failed config left the view showing "Loading…" for good, with no
        // way back to the list — the reader's only exit was closing the whole
        // enquiry.
        return (
            <div className="flex flex-col items-start gap-2 px-2 py-6">
                <p className="text-[0.8125rem] text-[#828282]">
                    {configFailed
                        ? `We could not open the new project form.`
                        : `Loading…`}
                </p>
                {configFailed && (
                    <button
                        type="button"
                        onClick={() => setCreating(false)}
                        className="text-[0.8125rem] font-medium text-[#161616] underline"
                    >
                        {`Back to your projects`}
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="relative">
                <Search
                    aria-hidden
                    className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-black/30"
                />
                <input
                    type="search"
                    value={keyword}
                    disabled={disabled}
                    aria-label="Search your projects"
                    placeholder="Search your projects"
                    onChange={(event) => setKeyword(event.target.value)}
                    className={cn(GATE_FIELD_INPUT_CLASS, 'w-full pl-9')}
                />
            </div>

            <div
                role="radiogroup"
                aria-label="Your projects"
                className="flex max-h-64 flex-col overflow-x-hidden overflow-y-auto overscroll-contain"
            >
                {matches.map((project) => {
                    const isSelected =
                        String(project.ID) === String(selectedId);
                    return (
                        <button
                            key={project.ID}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            disabled={disabled}
                            onClick={() => onSelect(project.ID)}
                            className={cn(
                                'flex items-center gap-3 rounded-[0.625rem] p-2 text-left disabled:opacity-50',
                                isSelected
                                    ? 'bg-[#f6f6f6]'
                                    : 'hover:bg-black/3',
                            )}
                        >
                            <ProjectThumbnail project={project} size={40} />
                            <span className="flex min-w-0 flex-1 flex-col">
                                <span className="truncate text-[0.875rem] font-medium text-[#161616]">
                                    {project.Title}
                                </span>
                                <span className="truncate text-[0.8125rem] text-[#828282]">
                                    {projectSubtitle(project)}
                                </span>
                            </span>
                            {project.Collaborators.length > 0 && (
                                // `shrink-0`: the cluster overlaps its avatars
                                // with negative margins, so a squeezed box
                                // spills them past the row rather than
                                // narrowing.
                                <AvatarCircles
                                    className="shrink-0"
                                    avatarUrls={project.Collaborators.map(
                                        (collaborator) => ({
                                            imageUrl:
                                                getCollaboratorAvatar(
                                                    collaborator,
                                                ),
                                            name:
                                                collaborator.name ||
                                                collaborator.email,
                                        }),
                                    )}
                                    maxAvatars={2}
                                    size="square24"
                                />
                            )}
                            <span
                                aria-hidden
                                className={cn(
                                    'size-4.5 shrink-0 rounded-full border',
                                    isSelected
                                        ? 'border-[6px] border-[#181818]'
                                        : 'border-black/20',
                                )}
                            />
                        </button>
                    );
                })}
                {!matches.length && (
                    <p className="px-2 py-6 text-center text-[0.8125rem] text-[#828282]">
                        {keyword.trim()
                            ? `No projects match “${keyword.trim()}”.`
                            : `You have no saved projects yet.`}
                    </p>
                )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-black/5 pt-2">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setCreating(true)}
                    className="flex items-center gap-1.5 text-[0.8125rem] font-medium text-[#161616] disabled:opacity-50"
                >
                    <Plus aria-hidden className="size-4" />
                    {`Add a new project`}
                </button>
                <span className="text-[0.75rem] text-[#828282]">
                    {projects.length === 1
                        ? `1 saved`
                        : `${projects.length} saved`}
                </span>
            </div>
        </div>
    );
};
